import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useMe as libUseMe,
  useLogin as libUseLogin,
  useLogout as libUseLogout,
  useChangePassword as libUseChangePassword,
} from "@marekl/adminer";
import { apiGet, apiPost, apiPut, ApiError } from "../lib/api";
import type { Dayjs } from "dayjs";

export type Role = "user" | "manager" | "superadmin";

export interface User {
  id: number;
  username: string;
  display_name: string;
  role: Role;
  email?: string;
  active: boolean;
  password_locked: boolean;
  last_login_at?: Dayjs;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

/** Fetches the currently authenticated user. Returns null if not logged in. */
export function useMe() {
  return libUseMe<User>();
}

/** Logs in and primes the me query cache with the returned user. */
export function useLogin() {
  return libUseLogin<User>();
}

/** Logs out and clears all cached query data. */
export function useLogout() {
  return libUseLogout();
}

/** Re-export of the lib's change-password mutation; payload is `{ oldPassword, newPassword }`. */
export const useChangePassword = libUseChangePassword;

interface UsersListResponse {
  users: User[];
}

/** Fetches all users (superadmin only). */
export function useUsers() {
  return useQuery<User[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await apiGet<UsersListResponse>("/users");
      return res.users;
    },
  });
}

export interface CreateUserPayload {
  username: string;
  display_name?: string;
  password: string;
  role: Role;
  email?: string;
  password_locked?: boolean;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, CreateUserPayload>({
    mutationFn: (payload) => apiPost<User>("/users", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface UpdateUserPayload {
  display_name?: string;
  role?: Role;
  email?: string;
  active?: boolean;
  password_locked?: boolean;
}

export function useUpdateUser(id: number) {
  const qc = useQueryClient();
  return useMutation<User, ApiError, UpdateUserPayload>({
    mutationFn: (payload) => apiPut<User>(`/users/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export interface ResetPasswordPayload {
  new_password: string;
}

export function useResetUserPassword(id: number) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ResetPasswordPayload>({
    mutationFn: (payload) => apiPost<void>(`/users/${id}/reset-password`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}
